import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { projectsAPI, usersAPI } from '../../services/api';
import useAuthStore from '../../context/authStore';
import { formatRoleLabel } from '../../utils/roles';

const getOrgInitials = (orgName) => {
  if (!orgName) return 'Client';
  const parts = orgName.trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase()).join('');
};

const getUserId = (user) => user?._id || user;

export default function ClientCollaboratorsModal({ projectId, documentName, onClose, onContinue }) {
  const { user } = useAuthStore();
  const [clientContacts, setClientContacts] = useState([]);
  const [clientLeadId, setClientLeadId] = useState('');
  const [selectedClientUserId, setSelectedClientUserId] = useState('');
  const [savingContacts, setSavingContacts] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('facility_worker');
  const [inviting, setInviting] = useState(false);
  const [showInviteValidation, setShowInviteValidation] = useState(false);

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['clientProjectForDraft', projectId],
    queryFn: () => projectsAPI.getById(projectId),
    select: (res) => res.data?.project || res.project || res.data,
    enabled: !!projectId,
  });

  const orgId = project?.clientId?._id || project?.clientId;
  const orgLabel = getOrgInitials(project?.clientId?.name);

  const { data: usersData = [] } = useQuery({
    queryKey: ['clientProjectUsers', orgId],
    queryFn: () => usersAPI.getAll(),
    select: (res) => res.data?.users || res.data || [],
    enabled: !!orgId,
  });

  const clientUsers = useMemo(() => {
    return (usersData || []).filter((person) => {
      const userOrgId = person.organizationId?._id || person.organizationId;
      return orgId && userOrgId?.toString() === orgId.toString();
    });
  }, [usersData, orgId]);

  const { data: pendingInvites = [], refetch: refetchInvites } = useQuery({
    queryKey: ['clientProjectInvites', orgId, projectId],
    queryFn: async () => {
      const res = await api.get('/invitations', {
        params: { organizationId: orgId, status: 'pending', projectId },
      });
      return res.data?.invitations || res.invitations || [];
    },
    enabled: !!orgId && !!projectId,
  });

  useEffect(() => {
    if (!project) return;
    const contacts = project.clientContacts || [];
    setClientContacts(contacts);
    const leadContact = contacts?.[0]?._id || contacts?.[0] || '';
    setClientLeadId(leadContact);
  }, [project]);

  const handleAddClient = () => {
    if (!selectedClientUserId) return;
    if (clientContacts.some((contact) => getUserId(contact) === selectedClientUserId)) {
      return;
    }
    const selectedUser = clientUsers.find((person) => person._id === selectedClientUserId);
    if (!selectedUser) return;
    const nextContacts = [...clientContacts, selectedUser];
    setClientContacts(nextContacts);
    if (!clientLeadId) {
      setClientLeadId(selectedClientUserId);
    }
    setSelectedClientUserId('');
  };

  const handleRemoveClient = (userId) => {
    const nextContacts = clientContacts.filter((contact) => getUserId(contact) !== userId);
    setClientContacts(nextContacts);
    if (clientLeadId === userId) {
      setClientLeadId(nextContacts[0]?._id || '');
    }
  };

  const handleSaveContacts = async () => {
    setShowValidation(true);
    if (clientContacts.length === 0) {
      onContinue?.();
      return;
    }
    const orderedContacts = clientLeadId
      ? [
        clientLeadId,
        ...clientContacts
          .map((contact) => getUserId(contact))
          .filter((contactId) => contactId !== clientLeadId),
      ]
      : clientContacts.map((contact) => getUserId(contact));

    if (!clientLeadId) {
      toast.error('Select a client lead before saving.');
      return;
    }

    if (!orderedContacts.includes(clientLeadId)) {
      toast.error('Client lead must be included in the collaborators list.');
      return;
    }

    try {
      setSavingContacts(true);
      await projectsAPI.updateContacts(projectId, orderedContacts);
      toast.success('Collaborators updated');
      onContinue?.();
    } catch (error) {
      toast.error(error.message || 'Failed to update collaborators');
    } finally {
      setSavingContacts(false);
    }
  };

  const handleInviteCollaborator = async () => {
    setShowInviteValidation(true);
    if (!inviteEmail || !inviteFirstName || !inviteLastName) {
      toast.error('Enter a name and email to invite a collaborator.');
      return;
    }

    try {
      setInviting(true);
      await api.post('/invitations', {
        email: inviteEmail,
        role: inviteRole,
        organizationId: orgId,
        projectId,
      });

      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('facility_worker');
      setShowInviteValidation(false);
      await refetchInvites();
      toast.success('Invite sent');
    } catch (error) {
      toast.error(error.message || 'Failed to invite collaborator');
    } finally {
      setInviting(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/invitations/${inviteId}/resend`);
      toast.success('Invitation resent');
      await refetchInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to resend invite');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/invitations/${inviteId}`);
      toast.success('Invitation revoked');
      await refetchInvites();
    } catch (error) {
      toast.error(error.message || 'Failed to revoke invite');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[calc(100dvh-2rem)] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Users className="text-teal-700" size={22} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Add collaborators</h2>
              <p className="text-gray-600 mt-1">
                Add your team members before reviewing {documentName || 'this draft'}.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                If you are the only reviewer, you can continue without adding anyone.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {projectLoading ? (
            <div className="text-sm text-gray-500">Loading collaborators...</div>
          ) : (
            <>
              {showValidation && clientContacts.length > 0 && !clientLeadId && (
                <p className="text-sm text-red-600">
                  Select a client lead before continuing.
                </p>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">{orgLabel} Collaborators</h3>
                  <span className="text-sm text-gray-500">
                    {clientContacts.length} selected
                  </span>
                </div>

                {clientContacts.length === 0 ? (
                  <p className="text-gray-500">No collaborators selected yet.</p>
                ) : (
                  clientContacts.map((contact) => {
                    const contactId = getUserId(contact);
                    const contactUser = contact._id
                      ? contact
                      : clientUsers.find((person) => person._id === contactId) || {};
                    const isLead = clientLeadId === contactId;
                    return (
                      <div
                        key={contactId}
                        className="flex flex-wrap items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <p className="font-medium text-gray-900">
                            {contactUser.firstName} {contactUser.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{contactUser.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isLead && (
                            <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">
                              Lead
                            </span>
                          )}
                          <button
                            onClick={() => handleRemoveClient(contactId)}
                            className="text-sm text-red-600 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
                <select
                  value={selectedClientUserId}
                  onChange={(e) => setSelectedClientUserId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select teammate...</option>
                  {clientUsers.map((person) => (
                    <option key={person._id} value={person._id}>
                      {person.firstName} {person.lastName}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddClient}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  <UserPlus size={16} />
                  Add
                </button>
              </div>

              {clientContacts.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {orgLabel} Lead
                  </label>
                  <select
                    value={clientLeadId}
                    onChange={(e) => setClientLeadId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {clientContacts.map((contact) => {
                      const contactId = getUserId(contact);
                      const contactUser = contact._id
                        ? contact
                        : clientUsers.find((person) => person._id === contactId) || {};
                      return (
                        <option key={contactId} value={contactId}>
                          {contactUser.firstName} {contactUser.lastName}
                        </option>
                      );
                    })}
                  </select>
                  {showValidation && !clientLeadId && (
                    <p className="text-sm text-red-600 mt-2">
                      Select a client lead to continue.
                    </p>
                  )}
                </div>
              )}

              {user?.role === 'facility_admin' && (
                <div className="rounded-lg border border-gray-200 p-4 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Invite collaborator</p>
                    <p className="text-xs text-gray-500">
                      Invite a teammate who does not have portal access yet.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={inviteFirstName}
                      onChange={(e) => setInviteFirstName(e.target.value)}
                      placeholder="First name"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="text"
                      value={inviteLastName}
                      onChange={(e) => setInviteLastName(e.target.value)}
                      placeholder="Last name"
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="Email address"
                      className="px-3 py-2 border border-gray-300 rounded-lg sm:col-span-2"
                    />
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg sm:col-span-2"
                    >
                      <option value="facility_worker">Facility Team Member (Training)</option>
                      <option value="facility_manager">Facility Manager</option>
                      <option value="facility_admin">Facility Admin</option>
                    </select>
                  </div>
                  {showInviteValidation && (!inviteFirstName.trim() || !inviteLastName.trim()) && (
                    <p className="text-sm text-red-600">First and last name are required.</p>
                  )}
                  {showInviteValidation && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail) && (
                    <p className="text-sm text-red-600">Enter a valid email address.</p>
                  )}
                  <div className="flex justify-end">
                    <button
                      onClick={handleInviteCollaborator}
                      disabled={inviting}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                    >
                      {inviting ? 'Inviting...' : 'Send Invite'}
                    </button>
                  </div>
                </div>
              )}

              {pendingInvites.length > 0 && (
                <div className="rounded-lg border border-gray-200 p-4 space-y-2">
                  <p className="text-sm font-medium text-gray-900">Pending Invites</p>
                  {pendingInvites.map((invite) => (
                    <div
                      key={invite._id || invite.id}
                      className="flex flex-wrap items-center justify-between gap-3 p-2 bg-yellow-50 rounded"
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-900">{invite.email}</p>
                        <p className="text-xs text-gray-500">{formatRoleLabel(invite.role)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                          Pending invite
                        </span>
                        <button
                          onClick={() => handleResendInvite(invite._id || invite.id)}
                          className="text-xs text-blue-600 hover:text-blue-700"
                        >
                          Resend
                        </button>
                        <button
                          onClick={() => handleRevokeInvite(invite._id || invite.id)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex flex-wrap gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveContacts}
            disabled={savingContacts || projectLoading}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:bg-gray-300"
          >
            {savingContacts ? 'Saving...' : 'Save Collaborators'}
          </button>
        </div>
      </div>
    </div>
  );
}
